import {useEffect, useMemo} from "react";

import {
    useGuideAudioStreamListener,
    type GuideAudioEventPayload,
} from "@khan/events-guide";
import {AudioCache} from "@khan/guide-core-helpers";
import type {MessageAudio} from "@khan/guide-types";

import {useGuideAudioController} from "../hooks/use-guide-audio-controller.ts";

/**
 * The key used to store the streaming audio in the audio cache
 */
export const STREAM_AUDIO_KEY = "stream_audio_key";

/**
 * The minimum length of the transcript before we start playing streaming audio.
 */
// NOTE: This number should be increased if we are reaching the end of the
// buffered audio before we receive another chunk or before we complete the
// stream.
export const STREAMING_BUFFER_MIN_SIZE = 70;

/**
 * Hook that listens to the audio stream event.
 */
export const useGuideAudioStream = () => {
    const {audioCache, audioKey, isVoiceOn, play, setAudioCache, stop} =
        useGuideAudioController();
    const currentBuffer = useMemo(
        () => audioCache.get(STREAM_AUDIO_KEY) ?? [],
        [audioCache],
    );

    useGuideAudioStreamListener((payload: GuideAudioEventPayload) => {
        // Type-refinement for TypeScript
        if ("isComplete" in payload) {
            // If we're done streaming audio, and we haven't played it yet
            // (our minimum buffer size > total chunks), play the audio.
            if (payload.isComplete) {
                // We'll only play the audio if the user has enabled Guide voice.
                isVoiceOn && play(STREAM_AUDIO_KEY);
                return;
            }
        } else {
            // In case there is no audio string, we do not update our stream
            // buffer
            if (!payload.base64Audio) {
                return;
            }

            setAudioCache((prevCache) => {
                const {isInitialChunk, ...audioChunk} = payload;
                // If this is the first chunk of the stream, we want to start
                // with an empty buffer.
                const prevBuffer = isInitialChunk
                    ? []
                    : (prevCache.get(STREAM_AUDIO_KEY) ?? []);

                // If we're streaming audio, and we get a new audio chunk,
                // stop any audio that is currently playing and prioritize
                // the incoming audio.
                //
                // We've seen a race condition where this is run right after
                // play(STREAM_AUDIO_KEY) and before the audioKey is set. In
                // that case, we don't want to stop the audio.
                if (
                    isInitialChunk ||
                    (audioKey !== "" && audioKey !== STREAM_AUDIO_KEY)
                ) {
                    stop();
                }

                const nextBuffer = [...prevBuffer, audioChunk];
                return new AudioCache(prevCache).set(
                    STREAM_AUDIO_KEY,
                    nextBuffer,
                );
            });
        }
    });

    // Checks whether we should play the audio stream when the buffer changes.
    useEffect(() => {
        if (isVoiceOn && _isSufficientlyBuffered(currentBuffer)) {
            play(STREAM_AUDIO_KEY);
        }
    }, [currentBuffer, play, isVoiceOn]);

    // Handle changes to the voice on/off state.
    const isStreaming = audioKey === STREAM_AUDIO_KEY;
    useEffect(() => {
        if (isStreaming && !isVoiceOn) {
            stop();
            return;
        }
    }, [isStreaming, stop, isVoiceOn]);
};

/**
 * Returns true if the buffer is sufficiently large to start playing the audio.
 */
const _isSufficientlyBuffered = (buffer: MessageAudio): boolean => {
    let transcriptLength = 0;
    for (const audioChunk of buffer) {
        transcriptLength += audioChunk.transcript.length;
        if (transcriptLength >= STREAMING_BUFFER_MIN_SIZE) {
            return true;
        }
    }
    return false;
};
